/**
 * Operations → Messages (#542 slice 2) — the global firm-communications view.
 *
 * Every Portal Messages thread, in the two honest lanes (#542 decision 2):
 * case-tagged threads (with a muted case-label chip for wayfinding) and
 * firm-level threads (untagged, labeled as such — no backfill guessing). Each
 * lane arrives from the worker unread-first, so the demands lead; a read
 * thread never buries an unread one.
 *
 * Search is the point (#415 — "a thread about one client can't be found
 * again"): as-you-type, client-side, over subject / message bodies / firm
 * name / case label. The corpus is fetch-all (~150 threads — the accepted
 * ceiling, revisit past a few thousand), so narrowing needs no server round
 * trip. Firm + case filters are ephemeral view state (the Coverage posture —
 * not URL params); their option lists derive from the corpus itself, so only
 * firms and cases that actually have threads are offered.
 *
 * Compose (the one primary action) opens the two-mode modal — see
 * `compose-message-modal.tsx`. Row rendering is the shared `FirmThreadList`
 * (one surface vocabulary with the case panel's Messages section).
 */

import { useMemo, useState } from "react";
import type { FirmThread } from "@command-center/shared";
import { useGlobalThreads } from "@/lib/use-firm-messages";
import { useGlobalCaseQuestions } from "@/lib/use-case-questions";
import { FirmThreadList } from "@/components/firm-thread-list";
import { CaseQuestionsLane } from "@/components/case-questions-lane";
import { ComposeMessageModal } from "@/components/compose-message-modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { SegmentedTab } from "@/components/ui/segmented-tab";

/** The two honest directions of firm communication (#613): Firm↔LF is the
 *  existing Portal Messages (OF/HF ↔ LegalFlare); Firm↔Firm is the Case
 *  Questions channel (OF/LF → HF). Separate corpora, one page. */
type CommsLane = "firm-lf" | "firm-firm";

export function MessagesPage() {
  const [lane, setLane] = useState<CommsLane>("firm-lf");
  const threads = useGlobalThreads();
  const caseQuestions = useGlobalCaseQuestions();
  const [query, setQuery] = useState("");
  const [firmFilter, setFirmFilter] = useState("");
  const [caseFilter, setCaseFilter] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);

  const caseTagged = threads.data?.caseTagged ?? [];
  const firmLevel = threads.data?.firmLevel ?? [];
  const questions = caseQuestions.data?.questions ?? [];
  // Subtab badges are watch signals — the demand on each direction.
  const firmLfUnread = [...caseTagged, ...firmLevel].filter((t) => t.unread).length;
  const openQuestionCount = questions.filter((q) => q.status === "Open").length;

  // Filter options derive from the corpus — only what actually has threads.
  const firmOptions = useMemo(
    () =>
      [...new Set([...caseTagged, ...firmLevel].map((t) => t.firm_name).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b),
      ),
    [caseTagged, firmLevel],
  );
  const caseOptions = useMemo(
    () =>
      [...new Set(caseTagged.map((t) => t.case_label).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [caseTagged],
  );

  const narrow = (list: FirmThread[]) =>
    list.filter(
      (t) =>
        (firmFilter === "" || t.firm_name === firmFilter) &&
        (caseFilter === "" || t.case_label === caseFilter) &&
        matchesQuery(t, query),
    );
  const shownCase = narrow(caseTagged);
  const shownFirm = narrow(firmLevel);
  const narrowed = query.trim() !== "" || firmFilter !== "" || caseFilter !== "";

  return (
    <section className="mx-auto max-w-4xl p-6" data-testid="messages-page">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl">Messages</h2>
        {/* Compose is the Firm↔LF primary action; Firm↔Firm has no global
            compose — LF asks a case question from the case detail panel. */}
        {lane === "firm-lf" ? (
          <Button onClick={() => setComposeOpen(true)} data-testid="messages-compose-open">
            New message
          </Button>
        ) : null}
      </div>

      <div
        role="tablist"
        aria-label="Message direction"
        data-testid="messages-lane-tabs"
        className="mb-6 inline-flex overflow-hidden rounded-md border border-border bg-muted/30"
      >
        <SegmentedTab
          active={lane === "firm-lf"}
          onClick={() => setLane("firm-lf")}
          label="Firm ↔ LF"
          count={firmLfUnread}
          testId="messages-lane-firm-lf"
        />
        <SegmentedTab
          active={lane === "firm-firm"}
          onClick={() => setLane("firm-firm")}
          label="Firm ↔ Firm"
          count={openQuestionCount}
          testId="messages-lane-firm-firm"
        />
      </div>

      {lane === "firm-firm" ? (
        <CaseQuestionsLane
          questions={questions}
          isLoading={caseQuestions.isLoading}
          isError={caseQuestions.isError}
        />
      ) : threads.isLoading ? (
        <p className="p-8 text-sm text-plane-muted">Loading messages…</p>
      ) : threads.isError ? (
        <p className="p-8 text-sm text-plane-muted" data-testid="messages-error">
          Couldn't load messages. Refresh to retry.
        </p>
      ) : (
        <>
          <div className="mb-6 flex flex-wrap items-center gap-2">
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search subject, message text, firm, or case…"
              aria-label="Search messages"
              data-testid="messages-search"
              className="max-w-sm"
            />
            <FilterSelect
              value={firmFilter}
              onChange={setFirmFilter}
              options={firmOptions}
              allLabel="All firms"
              testId="messages-firm-filter"
            />
            <FilterSelect
              value={caseFilter}
              onChange={setCaseFilter}
              options={caseOptions}
              allLabel="All cases"
              testId="messages-case-filter"
            />
            {narrowed ? (
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setFirmFilter("");
                  setCaseFilter("");
                }}
                className="text-xs text-muted-foreground hover:text-foreground"
                data-testid="messages-clear-filters"
              >
                Clear
              </button>
            ) : null}
          </div>

          {caseTagged.length === 0 && firmLevel.length === 0 ? (
            <p className="text-sm italic text-plane-muted" data-testid="messages-empty">
              No firm messages yet. Start one with New message.
            </p>
          ) : shownCase.length === 0 && shownFirm.length === 0 ? (
            <p className="text-sm italic text-plane-muted" data-testid="messages-empty-filtered">
              Nothing matches. Clear the search or filters to see every thread.
            </p>
          ) : (
            <div className="flex flex-col gap-8">
              <Lane title="Case threads" threads={shownCase} testId="messages-case-lane" showCaseChip />
              <Lane title="Firm threads" threads={shownFirm} testId="messages-firm-lane" />
            </div>
          )}
        </>
      )}

      <ComposeMessageModal open={composeOpen} onClose={() => setComposeOpen(false)} />
    </section>
  );
}

/** One lane: muted eyebrow header (count + unread count as facts) over the
 *  shared thread list. A lane emptied by filters says so quietly rather than
 *  vanishing — the operator should never wonder where a lane went. */
function Lane({
  title,
  threads,
  testId,
  showCaseChip = false,
}: {
  title: string;
  threads: FirmThread[];
  testId: string;
  showCaseChip?: boolean;
}) {
  const unread = threads.filter((t) => t.unread).length;
  return (
    <div data-testid={testId}>
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-plane-muted">
        {title}
        <span className="ml-2 font-normal normal-case tracking-normal" data-testid={`${testId}-count`}>
          {threads.length}
          {unread > 0 ? ` · ${unread} unread` : ""}
        </span>
      </h3>
      {threads.length === 0 ? (
        <p className="text-sm italic text-plane-muted" data-testid={`${testId}-empty`}>
          None match the current view.
        </p>
      ) : (
        <FirmThreadList threads={threads} testId={`${testId}-list`} showCaseChip={showCaseChip} />
      )}
    </div>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
  allLabel,
  testId,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  allLabel: string;
  testId: string;
}) {
  return (
    <NativeSelect
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={allLabel}
      data-testid={testId}
    >
      <option value="">{allLabel}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </NativeSelect>
  );
}

/** As-you-type search over the honest text: subject, every message body, the
 *  firm name, and the case label. Case-insensitive substring — 150 threads
 *  need no cleverer index. Exported for the unit test. */
export function matchesQuery(thread: FirmThread, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === "") return true;
  if (thread.subject.toLowerCase().includes(q)) return true;
  if (thread.firm_name.toLowerCase().includes(q)) return true;
  if (thread.case_label.toLowerCase().includes(q)) return true;
  return thread.messages.some((m) => m.body.toLowerCase().includes(q));
}
